import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  IndexTable,
  Text,
  Divider,
  Spinner,
  Banner,
  BlockStack,
  InlineStack,
  IndexFilters,
  useSetIndexFiltersMode,
  ChoiceList,
  Pagination,
  Select,
  TextField,
  Scrollable,
  Button,
  Icon,
  Badge,
  ActionList,
  Popover,
  Toast,
  Frame,
  Thumbnail,
  Tooltip,
  Modal,
  Tabs
} from '@shopify/polaris';
import { 
  PlusIcon,
  MenuHorizontalIcon,
  EditIcon,
  DeleteIcon,
  ViewIcon,
  ExportIcon
} from '@shopify/polaris-icons';
import axios from 'axios';
import { ApiErrorHandler, withApiErrorHandling } from '../utils/apiErrorHandler';
import CallLogImport from '../components/CallLogImport';
import CallCreationForm from '../components/CallCreationForm';
import CallAiAnalysis from './CallAiAnalysis';

interface CallLog {
  id: number;
  duration: number;
  call_type: string;
  status: string;
  direction: string;
  vendor?: string;
  call_sid?: string;
  called_at: string;
  phone?: string;
  called_from_number?: string;
  credits?: number;
  transcript?: string;
  transcript_analysis?: string;
  transcript_confidence?: number;
  transcript_metadata?: any;
  recording?: string;
  recording_url?: string;
  external_id?: string;
  created_at: string;
  updated_at: string;
  transcribed: boolean;
  has_ai_data?: boolean;
  duration_formatted?: string;
  // AI-related fields from backend (only transcription and translation are guaranteed)
  ai_transcription?: string;
  ai_translation?: string;
  // Prompt category information
  prompt_category?: {
    id: number;
    name: string;
  };
}

interface PaginationInfo {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface Filters {
  direction?: string[];
  vendor?: string[];
  transcription?: string[];
  status?: string[];
  category?: string[];
  startDate?: string;
  endDate?: string;
}

// Default views configuration
const defaultCallLogViews = [
  {
    id: 'all',
    name: 'All',
    filters: {},
  },
  {
    id: 'all_answered',
    name: 'All answered',
    filters: { status: 'answered' },
  },
  {
    id: 'answered_transcribed',
    name: 'Answered and transcribed',
    filters: { status: 'answered', transcription: 'yes' },
  },
  {
    id: 'answered_not_transcribed',
    name: 'Answered and not transcribed',
    filters: { status: 'answered', transcription: 'no' },
  },
  {
    id: 'not_answered',
    name: 'Not answered',
    filters: { status: 'not_answered' },
  },
];

// Sort options configuration
const callLogSortOptions = [
  { label: 'Newest first', value: 'created_at desc' as const, directionLabel: 'Newest first' },
  { label: 'Oldest first', value: 'created_at asc' as const, directionLabel: 'Oldest first' },
  { label: 'Duration (high to low)', value: 'duration desc' as const, directionLabel: 'Duration (high to low)' },
  { label: 'Duration (low to high)', value: 'duration asc' as const, directionLabel: 'Duration (low to high)' },
  { label: 'Status (A-Z)', value: 'status asc' as const, directionLabel: 'Status (A-Z)' },
  { label: 'Status (Z-A)', value: 'status desc' as const, directionLabel: 'Status (Z-A)' },
  { label: 'Vendor (A-Z)', value: 'vendor asc' as const, directionLabel: 'Vendor (A-Z)' },
  { label: 'Vendor (Z-A)', value: 'vendor desc' as const, directionLabel: 'Vendor (Z-A)' },
];

const CallLogs: React.FC = () => {
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [queryValue, setQueryValue] = useState('');
  const [sortSelected, setSortSelected] = useState(['created_at desc']);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    per_page: 15,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  // Filter states
  const [directionFilter, setDirectionFilter] = useState<string[]>([]);
  const [vendorFilter, setVendorFilter] = useState<string[]>([]);
  const [transcriptionFilter, setTranscriptionFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

  // Views and tabs state
  const [views, setViews] = useState([...defaultCallLogViews]);
  const [selectedView, setSelectedView] = useState(0);

  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [activeImportJob, setActiveImportJob] = useState<any>(null);
  const [checkingActiveJob, setCheckingActiveJob] = useState(false);

  // Create call modal state
  const [createCallModalOpen, setCreateCallModalOpen] = useState(false);

  // AI Analysis state
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  const [selectedCallLogId, setSelectedCallLogId] = useState<string | null>(null);
  const [analyzingCallLogId, setAnalyzingCallLogId] = useState<string | null>(null);
  

  
  const [actionPopovers, setActionPopovers] = useState<{ [key: number]: boolean }>({});
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const { mode, setMode } = useSetIndexFiltersMode();

  // Async dropdowns for vendors
  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([]);
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);
  const [vendorQuery, setVendorQuery] = useState("");

  // Categories state
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  const resourceName = {
    singular: 'call log',
    plural: 'call logs',
  };

  const sortOptions = [
    { label: 'Newest first', value: 'created_at desc', directionLabel: 'Newest first' },
    { label: 'Oldest first', value: 'created_at asc', directionLabel: 'Oldest first' },
    { label: 'Duration (high to low)', value: 'duration desc', directionLabel: 'Duration (high to low)' },
    { label: 'Duration (low to high)', value: 'duration asc', directionLabel: 'Duration (low to high)' },
  ];

  // Apply view filters
  const applyViewFilters = useCallback((view: any) => {
    if (view && view.filters) {
      // Apply view-specific filters
      if (view.filters.status) {
        setStatusFilter([view.filters.status]);
      } else {
        setStatusFilter([]);
      }
      if (view.filters.transcription) {
        setTranscriptionFilter([view.filters.transcription]);
      } else {
        setTranscriptionFilter([]);
      }
    } else {
      // Reset filters
      setStatusFilter([]);
      setTranscriptionFilter([]);
    }
  }, []);

  useEffect(() => {
    applyViewFilters(views[selectedView]);
  }, [selectedView, views, applyViewFilters]);

  // Initial data loading for vendors
  useEffect(() => {
    const loadInitialData = async () => {
      // Load initial vendors
      setIsLoadingVendors(true);
      const vendorsResult = await withApiErrorHandling(
        () => axios.get('/call_logs/vendors')
      ) as any;
      
      if (vendorsResult && vendorsResult.success) {
        setVendors(vendorsResult.data?.vendors || []);
      } else {
        setVendors([]);
      }
      setIsLoadingVendors(false);

    };

    loadInitialData();
  }, []);

  // Check for active import jobs on component mount
  useEffect(() => {
    const checkActiveJob = async () => {
      setCheckingActiveJob(true);
      try {
        const result = await withApiErrorHandling(
          () => axios.get('/import_jobs/active_job')
        ) as any;
        
        if (result && result.success) {
          setActiveImportJob(result.data);
        }
      } catch (error) {
        console.error('Error checking active import job:', error);
      } finally {
        setCheckingActiveJob(false);
      }
    };
    
    checkActiveJob();
  }, []);

  // Fetch vendors for async dropdown (search)
  useEffect(() => {
    if (!vendorQuery) return; // Skip if no query
    
    const timeoutId = setTimeout(async () => {
      setIsLoadingVendors(true);
      const result = await withApiErrorHandling(
        () => axios.get('/call_logs/vendors', { params: { query: vendorQuery } })
      ) as any;
      
      if (result && result.success) {
        setVendors(result.data?.vendors || []);
      } else {
        setVendors([]);
      }
      setIsLoadingVendors(false);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [vendorQuery]);


  const fetchCallLogs = useCallback(async (page = 1, query = '', filters: Filters = {}, sort = sortSelected[0]) => {
    setError(null);
    setLoading(true);
    
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: '15'
    });
    
    if (query) {
      params.append('query', query);
    }

    // Add sorting
    if (sort) {
      params.append('sort', sort);
    }

    // Add filters
    if (filters.direction && filters.direction.length > 0) {
      filters.direction.forEach(direction => {
        params.append('direction[]', direction);
      });
    }
    if (filters.vendor && filters.vendor.length > 0) {
      filters.vendor.forEach(vendor => {
        params.append('vendor[]', vendor);
      });
    }
    if (filters.transcription && filters.transcription.length > 0) {
      const transcribed = filters.transcription.includes('yes');
      params.append('transcribed', transcribed ? 'true' : 'false');
    }
    if (filters.status && filters.status.length > 0) {
      filters.status.forEach(status => {
        params.append('status[]', status);
      });
    }
    if (filters.category && filters.category.length > 0) {
      filters.category.forEach(category => {
        params.append('category[]', category);
      });
    }
    if (filters.startDate && filters.endDate) {
      params.append('start_date', filters.startDate);
      params.append('end_date', filters.endDate);
    }

    const result = await withApiErrorHandling(
      () => axios.get(`/call_logs?${params}`)
    ) as any;
    
    if (result && result.success) {
      setCallLogs(result.data.call_logs);
      setPagination(result.data.pagination);
    } else if (result && !result.success) {
      setError(result.error);
    } else {
      setError('Failed to fetch call logs');
    }
    
    setLoading(false);
  }, [sortSelected]);

  useEffect(() => {
    const filters = {
      direction: directionFilter,
      vendor: vendorFilter,
      transcription: transcriptionFilter,
      status: statusFilter,
      category: categoryFilter,
      startDate: undefined,
      endDate: undefined,
    };
    fetchCallLogs(pagination.page, queryValue, filters, sortSelected[0]);
  }, [fetchCallLogs, pagination.page, queryValue, directionFilter, vendorFilter, transcriptionFilter, statusFilter, categoryFilter, sortSelected]);

  const handleFiltersQueryChange = useCallback(
    (value: string) => {
      setQueryValue(value);
      setPagination(prev => ({ ...prev, page: 1 }));
    },
    []
  );

  const handleFiltersClearAll = useCallback(() => {
    setQueryValue('');
    setDirectionFilter([]);
    setVendorFilter([]);
    setTranscriptionFilter([]);
    setStatusFilter([]);
    setCategoryFilter([]);
    setPagination(prev => ({ ...prev, page: 1 }));
    // Apply current view filters after clearing
    applyViewFilters(views[selectedView]);
  }, [applyViewFilters, selectedView, views]);

  const handleSortChange = useCallback((value: string[]) => {
    setSortSelected(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const checkActiveImportJob = useCallback(async () => {
    setCheckingActiveJob(true);
    try {
      const result = await withApiErrorHandling(
        () => axios.get('/import_jobs/active_job')
      ) as any;
      
      if (result && result.success) {
        setActiveImportJob(result.data);
      }
    } catch (error) {
      console.error('Error checking active import job:', error);
    } finally {
      setCheckingActiveJob(false);
    }
  }, []);

  const handleImportComplete = useCallback(() => {
    // Refresh call logs after import
    fetchCallLogs(pagination.page, queryValue, {
      direction: directionFilter,
      vendor: vendorFilter,
      transcription: transcriptionFilter,
      status: statusFilter,
    }, sortSelected[0]);
    // Check for active jobs again
    checkActiveImportJob();
  }, [fetchCallLogs, pagination.page, queryValue, directionFilter, vendorFilter, transcriptionFilter, statusFilter, sortSelected, checkActiveImportJob]);

  const handleCreateCallSuccess = useCallback(() => {
    // Refresh call logs after creating a new call
    fetchCallLogs(pagination.page, queryValue, {
      direction: directionFilter,
      vendor: vendorFilter,
      transcription: transcriptionFilter,
      status: statusFilter,
    }, sortSelected[0]);
  }, [fetchCallLogs, pagination.page, queryValue, directionFilter, vendorFilter, transcriptionFilter, statusFilter, sortSelected]);

  // AI Analysis functions
  const handleAiAnalysis = (callLogId: string) => {
    setSelectedCallLogId(callLogId);
    setShowAiAnalysis(true);
    setActionPopovers({}); // Close all popovers
  };

  const closeAiAnalysis = () => {
    setShowAiAnalysis(false);
    setSelectedCallLogId(null);
  };

  // Trigger AI Analysis function
  const handleTriggerAnalysis = async (callLogId: string) => {
    try {
      setAnalyzingCallLogId(callLogId);
      setToastMessage('Starting AI analysis...');
      setToastActive(true);
      setActionPopovers({}); // Close all popovers
      
      const result = await withApiErrorHandling(
        () => axios.post(`/call_logs/${callLogId}/ai_analyze`)
      ) as any;
      
      if (result && result.success) {
        setToastMessage('AI analysis completed successfully!');
        setToastActive(true);
        // Refresh the call logs to show updated status
        fetchCallLogs(pagination.page, queryValue, {
          direction: directionFilter,
          vendor: vendorFilter,
          transcription: transcriptionFilter,
          status: statusFilter,
        }, sortSelected[0]);
      } else {
        setToastMessage(result?.error || 'Failed to start AI analysis');
        setToastActive(true);
      }
    } catch (error) {
      console.error('Error triggering AI analysis:', error);
      setToastMessage('Failed to start AI analysis');
      setToastActive(true);
    } finally {
      setAnalyzingCallLogId(null);
    }
  };



  const showToast = (message: string) => {
    setToastMessage(message);
    setToastActive(true);
    setTimeout(() => setToastActive(false), 3000);
  };

  const toggleActionPopover = useCallback((callLogId: number) => {
    setActionPopovers(prev => ({
      ...prev,
      [callLogId]: !prev[callLogId]
    }));
  }, []);







  const getStatusBadge = (status: string | null | undefined) => {
    if (!status) {
      return getColoredBadge('Unknown', 'info');
    }
    
    const statusColors: { [key: string]: 'success' | 'critical' | 'warning' | 'info' } = {
      answered: 'success',
      not_answered: 'critical',
    };
    
    const humanizedStatus = status
      .replace('_', ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return getColoredBadge(humanizedStatus, statusColors[status] || 'info');
  };

  const getDirectionBadge = (direction: string | null | undefined) => {
    if (!direction) {
      return getGrayBadge('Unknown');
    }
    return getGrayBadge(direction.charAt(0).toUpperCase() + direction.slice(1));
  };

  const getTranscribedBadge = (transcribed: boolean) => {
    if (transcribed) {
      return getGrayBadge('Transcribed');
    } else {
      return getGrayBadge('Not Transcribed');
    }
  };

  const getColoredBadge = (text: string, color: 'success' | 'critical' | 'warning' | 'info') => {
    const colorMap = {
      success: { bg: '#d4edda', text: '#155724', border: '#c3e6cb' },
      critical: { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' },
      warning: { bg: '#fff3cd', text: '#856404', border: '#ffeaa7' },
      info: { bg: '#d1ecf1', text: '#0c5460', border: '#bee5eb' }
    };

    const colors = colorMap[color];

    return (
      <span style={{
        backgroundColor: colors.bg,
        color: colors.text,
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500',
        border: `1px solid ${colors.border}`,
        display: 'inline-block',
        marginRight: '4px'
      }}>
        {text}
      </span>
    );
  };

  const getGrayBadge = (text: string) => {
    return (
      <span style={{
        backgroundColor: '#f6f6f7',
        color: '#202223',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500',
        border: '1px solid #e1e3e5',
        display: 'inline-block',
        marginRight: '4px'
      }}>
        {text}
      </span>
    );
  };

  const filters = [
    {
      key: 'direction',
      label: 'Direction',
      filter: (
        <ChoiceList
          title="Direction"
          titleHidden
          choices={[
            { label: 'Inbound', value: 'inbound' },
            { label: 'Outbound', value: 'outbound' },
          ]}
          selected={directionFilter}
          onChange={setDirectionFilter}
          allowMultiple={true}
        />
      ),
      shortcut: true,
    },
    {
      key: 'vendor',
      label: 'Vendor',
      filter: (
        <div>
          <div style={{ marginBottom: "12px" }}>
            <TextField
              label="Vendor search"
              labelHidden
              value={vendorQuery}
              onChange={setVendorQuery}
              placeholder="Search vendors..."
              autoComplete="off"
            />
          </div>
          {isLoadingVendors ? (
            <div style={{ padding: "16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              <Spinner size="small" />
              <span style={{ color: '#6d7175' }}>
                <Text variant="bodyMd" as="span">Loading vendors...</Text>
              </span>
            </div>
          ) : (
            <Scrollable shadow style={{ padding: "4px 0" }} height={240}>
              <ChoiceList
                title="Vendors"
                titleHidden
                choices={vendors.map((vendor) => ({
                  label: vendor.name,
                  value: vendor.id,
                }))}
                selected={vendorFilter}
                onChange={setVendorFilter}
                allowMultiple={true}
              />
            </Scrollable>
          )}
        </div>
      ),
      shortcut: true,
    },
    {
      key: 'transcription',
      label: 'Transcription',
      filter: (
        <ChoiceList
          title="Transcription Status"
          titleHidden
          choices={[
            { label: 'Transcribed', value: 'yes' },
            { label: 'Not Transcribed', value: 'no' },
          ]}
          selected={transcriptionFilter}
          onChange={setTranscriptionFilter}
          allowMultiple={true}
        />
      ),
      shortcut: true,
    },
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Call Status"
          titleHidden
          choices={[
            { label: 'Answered', value: 'answered' },
            { label: 'Not Answered', value: 'not_answered' },
          ]}
          selected={statusFilter}
          onChange={setStatusFilter}
          allowMultiple={true}
        />
      ),
      shortcut: true,
    },
    {
      key: 'category',
      label: 'Category',
      filter: (
        <ChoiceList
          title="Call Category"
          titleHidden
          choices={[
            { label: 'General Transcription', value: 'General Transcription' },
            { label: 'Customer Feedback', value: 'Customer Feedback' },
            { label: 'Pre-sales Analysis', value: 'Pre-sales Analysis' },
          ]}
          selected={categoryFilter}
          onChange={setCategoryFilter}
          allowMultiple={true}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = [
    ...(directionFilter.length > 0 ? [{ key: 'direction', label: `Direction: ${directionFilter.join(', ')}`, onRemove: () => setDirectionFilter([]) }] : []),
    ...(vendorFilter.length > 0 ? [{ key: 'vendor', label: `Vendor: ${vendorFilter.map(id => vendors.find(v => v.id === id)?.name || id).join(', ')}`, onRemove: () => setVendorFilter([]) }] : []),
    ...(transcriptionFilter.length > 0 ? [{ key: 'transcription', label: `Transcription: ${transcriptionFilter.map(t => t === 'yes' ? 'Transcribed' : 'Not Transcribed').join(', ')}`, onRemove: () => setTranscriptionFilter([]) }] : []),
    ...(statusFilter.length > 0 ? [{ key: 'status', label: `Status: ${statusFilter.map(s => s === 'answered' ? 'Answered' : 'Not Answered').join(', ')}`, onRemove: () => setStatusFilter([]) }] : []),
    ...(categoryFilter.length > 0 ? [{ key: 'category', label: `Category: ${categoryFilter.join(', ')}`, onRemove: () => setCategoryFilter([]) }] : []),
  ];

  // Build tabs from views
  const tabs = views.map((view, index) => ({
    id: view.id,
    content: view.name,
    accessibilityLabel: view.name,
    panelID: `${view.id}-call-logs`,
  }));

  const rowMarkup = callLogs.map(
    (callLog, index) => {
      const { id, duration, status, direction, vendor, transcript, created_at, recording, recording_url, call_sid, phone, external_id, transcribed, has_ai_data, prompt_category } = callLog;
      const phoneInfo = phone || external_id || 'No Phone Info';
      const phoneDetails = phone || external_id || 'No phone info';
      const durationFormatted = `${Math.floor(duration / 60)}m ${duration % 60}s`;
      const date = new Date(created_at);
      const dateFormatted = date.toLocaleDateString();
      const timeFormatted = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const actionPopoverActive = actionPopovers[id] || false;

      return (
        <IndexTable.Row
          id={id.toString()}
          key={id}
          position={index}
        >
          <IndexTable.Cell>
            <BlockStack gap="100">
              <Text variant="bodyMd" as="span" fontWeight="semibold">
                {phoneInfo}
              </Text>
              <Text variant="bodySm" as="span" tone="subdued">
                {phoneDetails}
              </Text>
              <Text variant="bodySm" as="span" tone="subdued">
                {call_sid}
              </Text>
              <InlineStack gap="200">
                {getStatusBadge(status)}
                {getDirectionBadge(direction)}
                {getTranscribedBadge(transcribed)}
                {has_ai_data && <Badge tone="success">AI Analyzed</Badge>}
              </InlineStack>
            </BlockStack>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <BlockStack gap="100">
              <Text variant="bodyMd" as="span" fontWeight="semibold">
                {durationFormatted}
              </Text>
              <Text variant="bodySm" as="span" tone="subdued">
                {vendor}
              </Text>
            </BlockStack>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Text variant="bodyMd" as="span">
              {prompt_category?.name || 'No Category'}
            </Text>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <BlockStack gap="100">
              <Text variant="bodyMd" as="span">
                {dateFormatted}
              </Text>
              <Text variant="bodySm" as="span" tone="subdued">
                {timeFormatted}
              </Text>
            </BlockStack>
          </IndexTable.Cell>
          <IndexTable.Cell>
            {recording_url && (
              <audio 
                controls 
                style={{ width: '200px', height: '32px' }}
                preload="metadata"
                onError={(e) => {
                  console.error('Audio loading error:', e);
                  console.log('Recording URL:', recording_url);
                }}
                onLoadStart={() => console.log('Loading audio from:', recording_url)}
              >
                <source src={recording_url} type="audio/mpeg" />
                <source src={recording_url} type="audio/mp3" />
                <source src={recording_url} type="audio/wav" />
                <source src={recording_url} type="audio/m4a" />
                Your browser does not support the audio element.
              </audio>
            )}
          </IndexTable.Cell>
          <IndexTable.Cell>
            <InlineStack gap="200">
              <Popover
                active={actionPopoverActive}
                activator={
                  <Button
                    icon={MenuHorizontalIcon}
                    variant="tertiary"
                    size="slim"
                    onClick={() => toggleActionPopover(id)}
                    accessibilityLabel="Actions"
                  />
                }
                onClose={() => toggleActionPopover(id)}
                preferredPosition="below"
                preferredAlignment="right"
              >
                <ActionList
                  actionRole="menuitem"
                  items={[
                    // Show "Analyze" for calls that haven't been transcribed or AI analyzed
                    ...(!transcribed || !has_ai_data ? [{
                      content: analyzingCallLogId === id.toString() ? 'Analyzing...' : 'Analyze',
                      icon: ViewIcon,
                      onAction: () => {
                        handleTriggerAnalysis(id.toString());
                      },
                      disabled: analyzingCallLogId === id.toString()
                    }] : []),
                    // Show "AI Analysis" for calls that have been analyzed (to view results)
                    ...(transcribed && has_ai_data ? [{
                      content: 'AI Analysis',
                      icon: ViewIcon,
                      onAction: () => {
                        handleAiAnalysis(id.toString());
                      }
                    }] : []),
                    {
                      content: 'Download Recording',
                      icon: ExportIcon,
                      onAction: () => {
                        if (recording_url) {
                          window.open(recording_url, '_blank');
                        } else {
                          showToast('No recording available');
                        }
                        toggleActionPopover(id);
                      },
                      disabled: !recording_url
                    },
                    {
                      content: 'Edit',
                      icon: EditIcon,
                      onAction: () => {
                        toggleActionPopover(id);
                      }
                    },
                    {
                      content: 'Delete',
                      icon: DeleteIcon,
                      destructive: true,
                      onAction: () => {
                        toggleActionPopover(id);
                      }
                    }
                  ]}
                />
              </Popover>
            </InlineStack>
          </IndexTable.Cell>
        </IndexTable.Row>
      );
    },
  );

  if (loading && callLogs.length === 0) {
    return (
      <Page title="Call Logs" fullWidth>
        <Layout>
            <Card>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <Text variant="bodyMd" as="p">Loading call logs...</Text>
              </div>
            </Card>
        </Layout>
      </Page>
    );
  }

  if (error && callLogs.length === 0) {
    return (
      <Page title="Call Logs" fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page 
      title="Call Logs" 
      fullWidth
      primaryAction={{
        content: 'Create Call',
        icon: PlusIcon,
        onAction: () => setCreateCallModalOpen(true),
      }}
      secondaryActions={[
        {
          content: activeImportJob ? 'Import in Progress...' : 'Import',
          onAction: () => setImportModalOpen(true),
          disabled: !!activeImportJob || checkingActiveJob,
        }
      ]}
    >
      {activeImportJob && (
        <div style={{ marginBottom: '16px', width: '100%' }}>
          <Banner tone="info">
            <p>
              An import job is currently in progress. Progress: {activeImportJob.progress}% 
              ({activeImportJob.processed_records || 0} of {activeImportJob.total_records || 0} records processed)
            </p>
          </Banner>
        </div>
      )}
      <Layout>
        <Layout.Section>
          <Card>
            <IndexFilters
              queryValue={queryValue}
              queryPlaceholder="Search call logs by phone, vendor, or call SID"
              onQueryChange={handleFiltersQueryChange}
              onQueryClear={() => setQueryValue('')}
              cancelAction={{
                onAction: handleFiltersClearAll,
                disabled: false,
                loading: false,
              }}
              tabs={tabs}
              selected={selectedView}
              onSelect={(selectedTabIndex) => {
                setSelectedView(selectedTabIndex);

                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              filters={filters}
              appliedFilters={appliedFilters}
              onClearAll={handleFiltersClearAll}
              mode={mode}
              setMode={setMode}
              sortOptions={callLogSortOptions}
              sortSelected={sortSelected}
              onSort={handleSortChange}
              primaryAction={undefined}
              canCreateNewView={false}
            />
            <div style={{ maxHeight: '75vh', overflow: 'auto' }}>
            <IndexTable
              resourceName={resourceName}
              itemCount={callLogs.length}
              selectable={false}
              headings={[
                { title: 'Phone & Status' },
                { title: 'Duration & Vendor' },
                { title: 'Category' },
                { title: 'Date' },
                { title: 'Recording' },
                { title: 'Actions' },
              ]}
              emptyState={
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    {queryValue || appliedFilters.length > 0
                      ? 'No call logs found matching your search criteria.' 
                      : 'No call logs found. Call logs will appear here when calls are made.'}
                  </Text>
                </div>
              }
            >
              {rowMarkup}
            </IndexTable>
            </div>
            
            {pagination.total_pages > 1 && (
              <div style={{ background: '#fff', borderTop: '1px solid #e1e3e5', padding: '12px', display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  hasPrevious={pagination.has_prev}
                  onPrevious={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  hasNext={pagination.has_next}
                  onNext={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  label={`Page ${pagination.page} of ${pagination.total_pages}`}
                />
              </div>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      <CallLogImport
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportComplete={handleImportComplete}
        onJobStatusChange={checkActiveImportJob}
      />

      <CallCreationForm
        open={createCallModalOpen}
        onClose={() => setCreateCallModalOpen(false)}
        onSuccess={handleCreateCallSuccess}
      />

      {showAiAnalysis && selectedCallLogId && (
        <CallAiAnalysis
          callLogId={selectedCallLogId}
          onClose={closeAiAnalysis}
        />
      )}




      {toastActive && (
        <Toast
          content={toastMessage}
          onDismiss={() => setToastActive(false)}
          duration={3000}
        />
      )}
    </Page>
  );
};

export default CallLogs;
